export const UTM_STORAGE_KEY = '_atrium_utm'

export const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

export type UtmParamKey = typeof UTM_PARAM_KEYS[number]
export type UtmParams = Partial<Record<UtmParamKey, string>>

export interface AttributionInput {
  utm_source?: string | null
  utm_medium?: string | null
}

export function readUtmParams(searchParams: URLSearchParams): UtmParams {
  const params: UtmParams = {}

  for (const key of UTM_PARAM_KEYS) {
    const value = searchParams.get(key)?.trim()
    if (value) params[key] = value
  }

  return params
}

export function hasUtmParams(params: UtmParams): boolean {
  return UTM_PARAM_KEYS.some(key => Boolean(params[key]))
}

function normalizeToken(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
}

function titleize(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

export function getAttributionSourceKey(input: AttributionInput): string {
  const source = normalizeToken(input.utm_source)
  const medium = normalizeToken(input.utm_medium)

  if (!source) return 'direct'

  if (['linkedin', 'linked in', 'li'].includes(source)) return 'linkedin'
  if (['google', 'google ads', 'googleadwords', 'adwords', 'gads'].includes(source)) {
    return medium.includes('display') ? 'google_display' : 'google_search'
  }
  if (['bing', 'microsoft', 'microsoft ads'].includes(source)) return 'bing_search'
  if (['facebook', 'fb', 'meta', 'instagram', 'ig'].includes(source)) return 'meta'
  if (['x', 'twitter'].includes(source)) return 'x'
  if (['newsletter', 'email', 'mailchimp', 'customer io', 'customer.io'].includes(source)) return 'email'
  if (['product hunt', 'producthunt'].includes(source)) return 'product_hunt'
  if (['reddit'].includes(source)) return 'reddit'
  if (['youtube'].includes(source)) return 'youtube'
  if (['tiktok', 'tik tok'].includes(source)) return 'tiktok'

  return source.replace(/\s+/g, '_')
}

export function getAttributionSourceLabel(input: AttributionInput): string {
  const key = getAttributionSourceKey(input)

  const labels: Record<string, string> = {
    direct: 'Direct',
    linkedin: 'LinkedIn',
    google_search: 'Google Search',
    google_display: 'Google Display',
    bing_search: 'Bing Search',
    meta: 'Meta',
    x: 'X',
    email: 'Email',
    product_hunt: 'Product Hunt',
    reddit: 'Reddit',
    youtube: 'YouTube',
    tiktok: 'TikTok',
  }

  return labels[key] ?? titleize(key)
}
